import { RepairJob, ShopSettings } from '@/types/models';

export interface PrintRepairJobSheetData {
  shop: Pick<ShopSettings, 'shop_name' | 'slogan' | 'address' | 'city' | 'phone' | 'phone_secondary' | 'phone_tertiary'>;
  repair: RepairJob;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return 'Rs. 0';
  return `Rs. ${amount.toLocaleString('en-PK')}`;
}

export function generateRepairJobSheetHtml(data: PrintRepairJobSheetData): string {
  const { shop, repair } = data;

  const phoneList: string[] = [];
  if (shop.phone) phoneList.push(shop.phone);
  if (shop.phone_secondary) phoneList.push(shop.phone_secondary);
  if (shop.phone_tertiary) phoneList.push(shop.phone_tertiary);

  const device = repair.customer_device || (repair.inventory_laptop ? {
    brand: repair.inventory_laptop.brand,
    model: repair.inventory_laptop.model,
    serial_number: repair.inventory_laptop.serial_number,
    cpu: repair.inventory_laptop.cpu,
    ram: repair.inventory_laptop.ram,
    storage: repair.inventory_laptop.storage,
    colour: repair.inventory_laptop.colour,
    intake_condition: repair.inventory_laptop.condition,
    accessories_received: '',
    charger_received: repair.inventory_laptop.charger_included,
    login_password_hint: null,
  } : null);

  const accessoriesList = repair.accessories_received || [];

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Job Sheet - ${repair.job_number}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #111827;
          background: #ffffff;
          margin: 0;
          padding: 0;
          font-size: 11.5px;
          line-height: 1.4;
        }
        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #16a34a; padding-bottom: 10px; margin-bottom: 12px; }
        .title { font-size: 18px; font-weight: 800; color: #16a34a; margin: 0; }
        .ticket-badge { background: #f0fdf4; border: 1.5px solid #16a34a; color: #166534; padding: 4px 10px; border-radius: 6px; font-size: 14px; font-weight: 800; font-family: monospace; display: inline-block; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
        .box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; }
        .box-title { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; margin: 0 0 6px 0; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; }
        .issue-box { background: #fffbeb; border: 1px solid #fef3c7; border-radius: 6px; padding: 10px; margin-bottom: 12px; }
        .financial-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; padding: 10px; margin-bottom: 14px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
        .terms { font-size: 9.5px; color: #4b5563; line-height: 1.3; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; margin-bottom: 16px; }
        .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 20px; }
        .sig-line { border-top: 1px dashed #4b5563; padding-top: 4px; text-align: center; font-size: 10px; font-weight: 600; color: #374151; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1 class="title">${shop.shop_name || 'AL KUWAIT TECH'}</h1>
          <p style="margin: 2px 0 0 0; color: #4b5563; font-size: 10.5px;">${shop.slogan || 'Repair & Service Center'}</p>
          <p style="margin: 2px 0 0 0; color: #6b7280; font-size: 10px;">${shop.address || ''}${shop.city ? ', ' + shop.city : ''}</p>
          ${phoneList.length > 0 ? `<p style="margin: 2px 0 0 0; color: #6b7280; font-size: 10px;">Tel: ${phoneList.join(' | ')}</p>` : ''}
        </div>
        <div style="text-align: right;">
          <div class="ticket-badge">REPAIR JOB #${repair.job_number}</div>
          <p style="margin: 4px 0 0 0; font-size: 10.5px; color: #4b5563;">Intake Date: <strong>${formatDate(repair.received_at)}</strong></p>
          <p style="margin: 2px 0 0 0; font-size: 10.5px; color: #4b5563;">Est. Delivery: <strong>${repair.estimated_completion ? formatDate(repair.estimated_completion) : 'TBD'}</strong></p>
        </div>
      </div>

      <div class="grid-2">
        <div class="box">
          <div class="box-title">Customer Details</div>
          <p style="margin: 0; font-size: 13px; font-weight: 700;">${repair.customer?.name || 'Customer'}</p>
          <p style="margin: 3px 0 0 0; color: #4b5563;">Phone: <strong>${repair.customer?.phone || 'N/A'}</strong></p>
          ${repair.customer?.email ? `<p style="margin: 2px 0 0 0; color: #4b5563;">Email: ${repair.customer.email}</p>` : ''}
          ${repair.customer?.address ? `<p style="margin: 2px 0 0 0; color: #4b5563;">Address: ${repair.customer.address}</p>` : ''}
        </div>

        <div class="box">
          <div class="box-title">Device & Intake Information</div>
          <p style="margin: 0; font-size: 13px; font-weight: 700;">${device ? `${device.brand || ''} ${device.model || ''}`.trim() : 'Customer Device'}</p>
          <p style="margin: 3px 0 0 0; color: #4b5563;">S/N or Service Tag: <strong>${device?.serial_number || 'N/A'}</strong></p>
          ${device?.cpu || device?.ram || device?.storage ? `<p style="margin: 2px 0 0 0; font-size: 10px; color: #6b7280;">Specs: ${[device.cpu, device.ram, device.storage].filter(Boolean).join(' • ')}</p>` : ''}
          <p style="margin: 2px 0 0 0; color: #4b5563;">Intake Condition: <strong>${device?.intake_condition || 'Normal wear'}</strong></p>
          <p style="margin: 2px 0 0 0; color: #4b5563;">Accessories Received: <strong>${accessoriesList.length > 0 ? accessoriesList.join(', ') : 'None'}</strong></p>
        </div>
      </div>

      <div class="issue-box">
        <div style="font-size: 10px; font-weight: 700; color: #92400e; text-transform: uppercase; margin-bottom: 4px;">Reported Fault / Customer Issue</div>
        <p style="margin: 0; font-size: 12px; font-weight: 600; color: #78350f;">${repair.reported_issue}</p>
        ${repair.intake_notes ? `<p style="margin: 4px 0 0 0; font-size: 10.5px; color: #92400e;">Intake Notes: ${repair.intake_notes}</p>` : ''}
      </div>

      ${repair.diagnosis ? `
        <div class="box" style="margin-bottom: 12px;">
          <div class="box-title">Technician Initial Diagnosis</div>
          <p style="margin: 0; font-size: 11.5px; color: #1f2937;">${repair.diagnosis}</p>
        </div>
      ` : ''}

      <div class="financial-box">
        <div>
          <span style="font-size: 9.5px; text-transform: uppercase; color: #166534; font-weight: 700; display: block;">Est. Labour</span>
          <strong style="font-size: 13px; color: #14532d;">${formatMoney(repair.estimated_labour || repair.labour_charge)}</strong>
        </div>
        <div>
          <span style="font-size: 9.5px; text-transform: uppercase; color: #166534; font-weight: 700; display: block;">Est. Parts</span>
          <strong style="font-size: 13px; color: #14532d;">${formatMoney(repair.estimated_parts || repair.parts_charge)}</strong>
        </div>
        <div>
          <span style="font-size: 9.5px; text-transform: uppercase; color: #166534; font-weight: 700; display: block;">Total Estimate / Deposit</span>
          <strong style="font-size: 14px; color: #15803d;">${formatMoney(repair.estimated_charge || repair.total_charge)} (Paid: ${formatMoney(repair.amount_paid)})</strong>
        </div>
      </div>

      <div class="terms">
        <strong style="color: #111827; display: block; margin-bottom: 3px;">TERMS & CONDITIONS OF REPAIR SERVICE:</strong>
        1. Please present this job sheet upon device collection.<br/>
        2. Devices not collected within 30 days of completion notification may incur storage charges or disposal per shop policy.<br/>
        3. Shop is not liable for existing data loss. Customers are advised to backup essential personal data prior to hardware repairs.<br/>
        4. Repair warranty covers only replaced parts and labor for the specified issue for 30 days from collection.
      </div>

      <div class="signatures">
        <div>
          <div style="height: 35px;"></div>
          <div class="sig-line">Customer Signature & Acceptance</div>
        </div>
        <div>
          <div style="height: 35px;"></div>
          <div class="sig-line">Authorized Technician / Shop Representative</div>
        </div>
      </div>
    </body>
    </html>
  `;
}

export function printRepairJobSheet(data: PrintRepairJobSheetData): void {
  const html = generateRepairJobSheetHtml(data);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow?.focus();
  setTimeout(() => {
    iframe.contentWindow?.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 300);
}
